/**
 * The company's roles and effective default role for the invite screens (SC-439). Names come
 * from the roles API, never from this bundle, because a platform admin can rename any role.
 *
 * One request per time the screen opens (`enabled` turns true): no polling, no cache kept after
 * it settles, so a failure is retried on the next open. Screens that open together share the
 * request through `fetchCompanyRolesWithDefault`.
 */
import { useEffect, useState } from 'react'
import { fetchCompanyRolesWithDefault, type RbacRole } from '../services/rbacService'

export interface InviteRolesState {
  status: 'idle' | 'loading' | 'ready' | 'error'
  roles: RbacRole[]
  defaultRoleKey?: string
}

const IDLE: InviteRolesState = { status: 'idle', roles: [] }

export function useInviteRoles(
  companyId: string | null | undefined,
  enabled: boolean
): InviteRolesState {
  const [state, setState] = useState<InviteRolesState>(IDLE)

  useEffect(() => {
    if (!enabled || !companyId) {
      setState(IDLE)
      return
    }
    let cancelled = false
    setState({ status: 'loading', roles: [] })
    fetchCompanyRolesWithDefault(companyId).then(
      (result) => {
        if (!cancelled) setState({ status: 'ready', ...result })
      },
      () => {
        if (!cancelled) setState({ status: 'error', roles: [] })
      }
    )
    return () => {
      cancelled = true
    }
  }, [companyId, enabled])

  return state
}
